import * as React from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { ChevronDown, ChevronRight, GripVertical, Plus, SquarePen, Trash2 } from 'lucide-react';
import { IconButton, Input } from '@ixicare/ui';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import {
  OrganizationNodeProps,
  OrganizationSiteSummaryProps,
  ProfileType,
  Site,
  SiteBody,
  SiteEntryProps,
} from '@ixicare/resources';
import { useQueryClient } from '@tanstack/react-query';
import { t } from '~/i18n';
import { useStores } from '~/store/utils';
import { UserAction } from '~/config/notification';
import {
  collectDescendantIds,
  findOrganizationById,
  findOrganizationOfSite,
  flattenOrganizations,
  ORGANIZATIONS_QUERY_KEY,
  useCreateOrganization,
  useDeleteOrganization,
  useUpdateOrganization,
} from '../../hooks/useOrganizations';
import styles from './OrganizationTree.module.css';

const DRAG_ORG_PREFIX = 'org:';
const DRAG_SITE_PREFIX = 'site:';
const DROP_ROOT_ID = 'root';

const dragTransformStyle = (
  draggable: ReturnType<typeof useDraggable>,
  editMode: boolean,
  options: { withOpacity?: boolean } = {}
): React.CSSProperties | undefined => {
  if (!editMode || !draggable.transform) return undefined;
  const style: React.CSSProperties = {
    transform: `translate3d(${draggable.transform.x}px, ${draggable.transform.y}px, 0)`,
  };
  if (options.withOpacity) style.opacity = draggable.isDragging ? 0.5 : 1;
  return style;
};

interface OrganizationTreeProps {
  organizations: OrganizationNodeProps[];
  unassignedSites: SiteEntryProps[];
  editMode: boolean;
  scopeRootId: string | null;
  siteUuidToId: Map<string, number>;
  onSitesChanged: () => void;
}

const OrganizationTree: React.FC<OrganizationTreeProps> = ({
  organizations,
  unassignedSites,
  editMode,
  scopeRootId,
  siteUuidToId,
  onSitesChanged,
}) => {
  const { notifications, confirmation, auth } = useStores();
  const isEmployee = auth.userType === ProfileType.WatcherrEmployee;
  const queryClient = useQueryClient();
  const createOrg = useCreateOrganization();
  const updateOrg = useUpdateOrganization();
  const deleteOrg = useDeleteOrganization();

  const notifyError = React.useCallback(
    (messageKey: string) =>
      notifications.addUserActionNotification({
        type: UserAction.SiteUpdate,
        notificationType: 'error',
        message: t(messageKey),
      }),
    [notifications]
  );

  const [activeDrag, setActiveDrag] = React.useState<{
    kind: 'org' | 'site';
    id: string;
    label: string;
  } | null>(null);
  const [rootAdding, setRootAdding] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());
  // Employees see every org, which can be a large tree — start it fully collapsed
  // so they expand only what they need. Runs once, after the tree first loads, so
  // it doesn't fight manual expand/collapse afterwards.
  const didInitCollapse = React.useRef(false);
  React.useEffect(() => {
    if (didInitCollapse.current || !isEmployee || organizations.length === 0) return;
    setCollapsed(new Set(flattenOrganizations(organizations).map(({ node }) => node.id)));
    didInitCollapse.current = true;
  }, [isEmployee, organizations]);
  const toggleCollapsed = React.useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    if (id.startsWith(DRAG_ORG_PREFIX)) {
      const orgId = id.slice(DRAG_ORG_PREFIX.length);
      const org = findOrganizationById(organizations, orgId);
      if (org) setActiveDrag({ kind: 'org', id: orgId, label: org.name });
    } else if (id.startsWith(DRAG_SITE_PREFIX)) {
      const siteUuid = id.slice(DRAG_SITE_PREFIX.length);
      const label =
        findOrganizationOfSite(organizations, siteUuid)?.sites.find((s) => s.uuid === siteUuid)?.name ??
        unassignedSites.find((s) => s.uuid === siteUuid)?.name ??
        siteUuid;
      setActiveDrag({ kind: 'site', id: siteUuid, label });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId.startsWith(DRAG_SITE_PREFIX)) {
      const siteUuid = activeId.slice(DRAG_SITE_PREFIX.length);
      const siteId = siteUuidToId.get(siteUuid);
      if (siteId === undefined) return;
      let newOrgId: string | null;
      if (overId === DROP_ROOT_ID) {
        newOrgId = scopeRootId;
      } else if (overId.startsWith(DRAG_ORG_PREFIX)) {
        newOrgId = overId.replace(DRAG_ORG_PREFIX, '');
      } else {
        return;
      }
      Site.patchSite(siteId, { organizationId: newOrgId } as SiteBody)
        .then(() => {
          notifications.addUserActionNotification({
            type: UserAction.SiteUpdate,
            notificationType: 'success',
            message: t('alerts.site.update.success', { siteName: activeDrag?.label ?? '' }),
          });
          queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
          onSitesChanged();
        })
        .catch(() => notifyError('siteSettings.organization.dnd.siteMoveFailed'));
      return;
    }

    if (activeId.startsWith(DRAG_ORG_PREFIX)) {
      const orgId = activeId.slice(DRAG_ORG_PREFIX.length);
      const targetOrgId = overId === DROP_ROOT_ID ? scopeRootId : overId.replace(DRAG_ORG_PREFIX, '');
      if (targetOrgId === orgId) return;
      const movingOrg = findOrganizationById(organizations, orgId);
      if (!movingOrg) return;
      if (movingOrg.parentId === targetOrgId) return;
      if (targetOrgId && collectDescendantIds(movingOrg).has(targetOrgId)) {
        notifyError('siteSettings.organization.dnd.cantNestSelf');
        return;
      }
      updateOrg.mutate({ id: orgId, body: { parentId: targetOrgId } });
    }
  };

  const handleCreateRoot = (name: string) => {
    setRootAdding(false);
    const trimmed = name.trim();
    if (!trimmed) return;
    createOrg.mutate(
      { name: trimmed, parentId: scopeRootId },
      { onError: () => notifyError('siteSettings.organization.create.failed') }
    );
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
      <div className={styles.Tree}>
        <RootDropZone editMode={editMode}>
          {organizations.map((org) => (
            <OrgNode
              key={org.id}
              org={org}
              depth={0}
              editMode={editMode}
              siteUuidToId={siteUuidToId}
              collapsed={collapsed}
              onToggleCollapsed={toggleCollapsed}
              onUpdateOrg={(id, body) => updateOrg.mutate({ id, body })}
              onDeleteOrg={(id, name) =>
                confirmation.show({
                  title: t('siteSettings.organization.delete.label'),
                  message: t('siteSettings.organization.delete.confirm', { name }),
                  confirmLabel: t('siteSettings.organization.delete.label'),
                  dangerous: true,
                  onConfirm: () =>
                    deleteOrg.mutate(id, {
                      onError: () => notifyError('siteSettings.organization.delete.failed'),
                    }),
                })
              }
              onCreateChild={(parentId, name) =>
                createOrg.mutate(
                  { name, parentId },
                  { onError: () => notifyError('siteSettings.organization.create.failed') }
                )
              }
            />
          ))}
        </RootDropZone>

        {editMode && (
          <div className={styles.RootAddRow}>
            {rootAdding ? (
              <InlineAddInput onSubmit={handleCreateRoot} onCancel={() => setRootAdding(false)} />
            ) : (
              <button type="button" className={styles.AddRootButton} onClick={() => setRootAdding(true)}>
                <Plus size={16} />
                {t('siteSettings.organization.add')}
              </button>
            )}
          </div>
        )}

        <UnassignedSection sites={unassignedSites} editMode={editMode} />
      </div>

      <DragOverlay>{activeDrag && <div className={styles.DragOverlay}>{activeDrag.label}</div>}</DragOverlay>
    </DndContext>
  );
};

interface RootDropZoneProps {
  editMode: boolean;
  children: React.ReactNode;
}

const RootDropZone: React.FC<RootDropZoneProps> = ({ editMode, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: DROP_ROOT_ID, disabled: !editMode });
  const className = [styles.RootDropZone, editMode && styles.RootDropZoneEditMode, isOver && styles.NodeRowIsOver]
    .filter(Boolean)
    .join(' ');
  return (
    <div ref={setNodeRef} className={className}>
      {children}
    </div>
  );
};

interface OrgNodeProps {
  org: OrganizationNodeProps;
  depth: number;
  editMode: boolean;
  siteUuidToId: Map<string, number>;
  onUpdateOrg: (id: string, body: { name?: string; parentId?: string | null }) => void;
  onDeleteOrg: (id: string, name: string) => void;
  onCreateChild: (parentId: string, name: string) => void;
  collapsed: Set<string>;
  onToggleCollapsed: (id: string) => void;
}

const OrgNode: React.FC<OrgNodeProps> = ({
  org,
  depth,
  editMode,
  siteUuidToId,
  onUpdateOrg,
  onDeleteOrg,
  onCreateChild,
  collapsed,
  onToggleCollapsed,
}) => {
  const [renaming, setRenaming] = React.useState(false);
  const [name, setName] = React.useState(org.name);
  const [adding, setAdding] = React.useState(false);

  React.useEffect(() => {
    setName(org.name);
  }, [org.name]);

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `${DRAG_ORG_PREFIX}${org.id}`,
    disabled: !editMode,
  });
  const draggable = useDraggable({
    id: `${DRAG_ORG_PREFIX}${org.id}`,
    disabled: !editMode,
  });

  const isEmpty = org.children.length === 0 && org.sites.length === 0;
  const isCollapsed = !editMode && collapsed.has(org.id);
  const canCollapse = !editMode && !isEmpty;

  const commitName = () => {
    const trimmed = name.trim();
    setRenaming(false);
    if (trimmed && trimmed !== org.name) {
      onUpdateOrg(org.id, { name: trimmed });
    } else {
      setName(org.name);
    }
  };

  return (
    <div className={`${styles.Node} ${isOver ? styles.NodeIsOver : ''}`} ref={setDropRef}>
      <div
        className={`${styles.NodeRow} ${canCollapse ? styles.NodeRowClickable : ''}`}
        ref={editMode ? draggable.setNodeRef : undefined}
        onClick={canCollapse ? () => onToggleCollapsed(org.id) : undefined}
        style={dragTransformStyle(draggable, editMode, { withOpacity: true })}
      >
        {editMode ? (
          <span className={styles.DragHandle} {...draggable.listeners} {...draggable.attributes}>
            <GripVertical size={16} />
          </span>
        ) : (
          <span className={styles.Chevron}>
            {canCollapse && (isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />)}
          </span>
        )}
        {renaming ? (
          <Input
            className={styles.InlineInput}
            label=""
            value={name}
            autoFocus
            onValueChange={setName}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitName();
              if (e.key === 'Escape') {
                setName(org.name);
                setRenaming(false);
              }
            }}
          />
        ) : (
          <span className={styles.OrgName}>{org.name}</span>
        )}
        {editMode && !renaming && (
          <span className={styles.InlineActions}>
            <span title={t('siteSettings.organization.rename')}>
              <IconButton lucide={SquarePen} onClick={() => setRenaming(true)} />
            </span>
            <span title={t('siteSettings.organization.add')}>
              <IconButton lucide={Plus} onClick={() => setAdding(true)} />
            </span>
            {isEmpty && (
              <span title={t('siteSettings.organization.delete.label')}>
                <IconButton lucide={Trash2} onClick={() => onDeleteOrg(org.id, org.name)} />
              </span>
            )}
          </span>
        )}
      </div>

      {!isCollapsed && (
        <div className={styles.Children}>
          {org.sites.map((site) => (
            <SiteRow key={site.uuid} site={site} editMode={editMode} siteId={siteUuidToId.get(site.uuid)} />
          ))}
          {adding && (
            <InlineAddInput
              onSubmit={(name) => {
                setAdding(false);
                if (name.trim()) onCreateChild(org.id, name.trim());
              }}
              onCancel={() => setAdding(false)}
            />
          )}
          {org.children.map((child) => (
            <OrgNode
              key={child.id}
              org={child}
              depth={depth + 1}
              editMode={editMode}
              siteUuidToId={siteUuidToId}
              onUpdateOrg={onUpdateOrg}
              onDeleteOrg={onDeleteOrg}
              onCreateChild={onCreateChild}
              collapsed={collapsed}
              onToggleCollapsed={onToggleCollapsed}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface SiteRowProps {
  site: OrganizationSiteSummaryProps;
  editMode: boolean;
  siteId: number | undefined;
}

const SiteRow: React.FC<SiteRowProps> = ({ site, editMode, siteId }) => {
  const draggable = useDraggable({
    id: `${DRAG_SITE_PREFIX}${site.uuid}`,
    disabled: !editMode,
  });

  return (
    <div
      className={`${styles.SiteRow} ${draggable.isDragging ? styles.SiteRowDragging : ''}`}
      ref={editMode ? draggable.setNodeRef : undefined}
      style={dragTransformStyle(draggable, editMode)}
    >
      {editMode && (
        <span className={styles.DragHandle} {...draggable.listeners} {...draggable.attributes}>
          <GripVertical size={14} />
        </span>
      )}
      <span className={styles.SiteName}>{site.name}</span>
      {siteId !== undefined && (
        <Link to={`/sites/${site.uuid}`} title={t('siteSettings.table.edit')}>
          <IconButton lucide={SquarePen} />
        </Link>
      )}
    </div>
  );
};

interface UnassignedSectionProps {
  sites: SiteEntryProps[];
  editMode: boolean;
}

const UnassignedSection: React.FC<UnassignedSectionProps> = ({ sites, editMode }) => {
  if (sites.length === 0) return null;
  return (
    <div>
      <div className={styles.UnassignedHeader}>{t('siteSettings.organization.unassigned')}</div>
      <div className={styles.UnassignedList}>
        {sites.map((s) => (
          <SiteRow key={s.uuid} site={{ uuid: s.uuid, name: s.name }} editMode={editMode} siteId={s.id} />
        ))}
      </div>
    </div>
  );
};

interface InlineAddInputProps {
  onSubmit: (name: string) => void;
  onCancel: () => void;
}

const InlineAddInput: React.FC<InlineAddInputProps> = ({ onSubmit, onCancel }) => {
  const [value, setValue] = React.useState('');
  return (
    <div className={styles.AddChildInputRow}>
      <Input
        label=""
        autoFocus
        value={value}
        onValueChange={setValue}
        onBlur={() => (value.trim() ? onSubmit(value) : onCancel())}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit(value);
          if (e.key === 'Escape') onCancel();
        }}
        placeholder={t('siteSettings.organization.newNamePlaceholder')}
      />
    </div>
  );
};

export default observer(OrganizationTree);
